import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { useAppointmentStore } from '@/store/appointmentStore';
import { useAuthStore } from '@/store/authStore';
import { apiClient } from '@/utils/api';
import { toast } from 'sonner';

interface EditAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  appointment: {
    id: string;
    title: string;
    description?: string;
    clientId: string;
    userId?: string;
    startTime: string;
    endTime: string;
    location?: string;
    status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED';
  };
  onSuccess?: () => void;
}

export default function EditAppointmentDialog({
  open,
  onOpenChange,
  appointmentId,
  appointment,
  onSuccess,
}: EditAppointmentDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    userId: '',
    startTime: '',
    endTime: '',
    location: '',
    status: 'SCHEDULED' as 'SCHEDULED' | 'COMPLETED' | 'CANCELLED',
  });
  const [endTimeManuallyEdited, setEndTimeManuallyEdited] = useState(false);

  const { updateAppointment, loading } = useAppointmentStore();
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<Array<{ id: string; firstName: string; lastName: string; email: string }>>([]);

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  useEffect(() => {
    if (open && appointment) {
      // Fetch users (endpoint accessibile a tutti gli utenti autenticati)
      apiClient.get('/users/assignable')
        .then((response) => {
          const usersData = response.data?.data || [];
          setUsers(usersData);
        })
        .catch((error) => {
          console.error('Error fetching users:', error);
          if (currentUser) {
            setUsers([{
              id: currentUser.id,
              firstName: currentUser.firstName || '',
              lastName: currentUser.lastName || '',
              email: currentUser.email || '',
            }]);
          }
        });

      // Popola form con dati appuntamento esistente
      setFormData({
        title: appointment.title || '',
        description: appointment.description || '',
        userId: appointment.userId || '',
        startTime: formatDateTimeLocal(new Date(appointment.startTime)),
        endTime: formatDateTimeLocal(new Date(appointment.endTime)),
        location: appointment.location || '',
        status: appointment.status || 'SCHEDULED',
      });
      setEndTimeManuallyEdited(false);
    }
  }, [open, appointment, currentUser]);

  // Aggiorna automaticamente endTime quando cambia startTime (+30 minuti)
  useEffect(() => {
    if (formData.startTime && !endTimeManuallyEdited && open) {
      const startDate = new Date(formData.startTime);
      const endDate = new Date(startDate.getTime() + 30 * 60 * 1000); // +30 minuti
      setFormData(prev => ({ ...prev, endTime: formatDateTimeLocal(endDate) }));
    }
  }, [formData.startTime, endTimeManuallyEdited, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.startTime) {
      toast.error('Titolo e data/ora di inizio sono obbligatori');
      return;
    }

    // Validazione date retroattive
    const startTime = new Date(formData.startTime);
    const now = new Date();
    if (startTime < now) {
      toast.error('La data/ora di inizio non può essere nel passato');
      return;
    }

    if (formData.endTime) {
      const endTime = new Date(formData.endTime);
      if (endTime < startTime) {
        toast.error('La data/ora di fine deve essere successiva a quella di inizio');
        return;
      }
    }

    try {
      await updateAppointment(appointmentId, {
        title: formData.title,
        description: formData.description || undefined,
        userId: formData.userId || undefined,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: formData.endTime ? new Date(formData.endTime).toISOString() : undefined,
        location: formData.location || undefined,
        status: formData.status,
      });

      toast.success('Appuntamento aggiornato con successo');
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Errore nell\'aggiornamento dell\'appuntamento');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica Appuntamento</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Titolo *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Es: Incontro con cliente per ristrutturazione"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Descrizione</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descrizione dettagliata dell'appuntamento..."
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Data/Ora Inizio *</Label>
              <Input
                id="startTime"
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                min={formatDateTimeLocal(new Date())}
                required
              />
            </div>

            <div>
              <Label htmlFor="endTime">Data/Ora Fine</Label>
              <Input
                id="endTime"
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) => {
                  setFormData({ ...formData, endTime: e.target.value });
                  setEndTimeManuallyEdited(true);
                }}
                min={formData.startTime || formatDateTimeLocal(new Date())}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="location">Luogo</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Es: Ufficio, Indirizzo cliente..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Stato</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value as any })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SCHEDULED">Programmato</SelectItem>
                  <SelectItem value="COMPLETED">Completato</SelectItem>
                  <SelectItem value="CANCELLED">Annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="userId">Assegnatario</Label>
              <Combobox
                options={users.map(user => ({
                  value: user.id,
                  label: `${user.firstName} ${user.lastName}`,
                  description: user.email || undefined,
                }))}
                value={formData.userId}
                onValueChange={(value) => setFormData({ ...formData, userId: value })}
                placeholder="Seleziona assegnatario (opzionale)"
                searchPlaceholder="Cerca utente..."
                emptyMessage="Nessun utente trovato"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={loading || !formData.title || !formData.startTime}>
              {loading ? 'Aggiornamento...' : 'Aggiorna Appuntamento'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

